/**
 * Enter Email Step (V2)
 *
 * Single-field form: just the patient's email. Triggers the OTP send.
 *
 * IMPORTANT: this is the ONLY PHI we collect before identity verification.
 * The backend treats every email as if it might match — never echo back
 * a "not found" hint here.
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

const schema = z.object({
  email: z.string().trim().toLowerCase().email('Please enter a valid email'),
});
type EmailFormData = z.infer<typeof schema>;

interface EnterEmailStepProps {
  initialEmail?: string;
  onSubmit: (email: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

export const EnterEmailStep: React.FC<EnterEmailStepProps> = ({
  initialEmail = '',
  onSubmit,
  loading,
  error,
}) => {
  const form = useForm<EmailFormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: initialEmail },
    mode: 'onChange',
  });

  const handleFormSubmit = async (data: EmailFormData) => {
    await onSubmit(data.email);
  };

  return (
    <div>
      <h2 className='mb-2 text-2xl font-bold text-slate-900'>Confirm your email</h2>
      <p className='mb-6 text-slate-500'>
        We&apos;ll send you a 6-digit code to verify it&apos;s really you.
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className='space-y-4'>
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel className='text-sm font-medium text-slate-700'>
                  Email address <span className='text-red-500'>*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type='email'
                    autoComplete='email'
                    inputMode='email'
                    placeholder='your@email.com'
                    className='rounded-lg'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error && (
            <div className='rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
              {error}
            </div>
          )}

          <Button
            type='submit'
            disabled={!form.formState.isValid || loading}
            className='h-11 w-full rounded-lg bg-teal-600 text-white hover:bg-teal-700'
          >
            {loading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Sending code...
              </>
            ) : (
              'Send verification code'
            )}
          </Button>

          <p className='flex items-start gap-2 text-xs text-slate-500'>
            <Shield className='mt-0.5 h-3.5 w-3.5 flex-shrink-0' />
            <span>
              We&apos;ll only use this email to confirm your appointment and contact you about
              your visit. We never share your information.
            </span>
          </p>
        </form>
      </Form>
    </div>
  );
};
